(function () {
    'ngInject';

    angular.module('app.shared.services').factory('CanvasUtilService', canvasUtils);

    function canvasUtils(ProductService, GeneralMathUtils) {
        /**
         * Now make sure the canvas is not too big because it can impact performance or produce weird behaviour.
         * So, we scale it down and compensate it with setViewportTransform(). The canvasWidth and canvasHeight dimensions
         *  don't change at all, we just scale down the canvas to show it to the user.
         * @param {Object} canvas fabric.js canvas
         * @param {Number} maxCanvasSize max canvas size in pixels
         * @param {Number} minCanvasSize min canvas size in pixels
         */
        function applyCanvasScaling(canvas, canvasWidth, canvasHeight, maxCanvasSize, minCanvasSize) {
            let minSizeAltered = false;
            if (canvasWidth < minCanvasSize || canvasHeight < minCanvasSize) {
                minSizeAltered = true;
                const ratio = Math.max(canvasWidth / minCanvasSize, canvasHeight / minCanvasSize);
                const scaledWidth = canvasWidth / ratio;
                const scaledHeight = canvasHeight / ratio;

                console.log('Scaling up canvas to: ' + parseInt(scaledWidth) + 'x' + parseInt(scaledHeight));

                canvas.setArtworkSize(scaledWidth, scaledHeight, false);
                canvas.setZoom(1 / ratio);
                canvas.fullVisibilityZoom = 1 / ratio;

                canvasWidth = scaledWidth;
                canvasHeight = scaledHeight;
            }

            if (canvasWidth > maxCanvasSize || canvasHeight > maxCanvasSize) {
                const ratio = Math.max(canvasWidth / maxCanvasSize, canvasHeight / maxCanvasSize);
                const scaledWidth = canvasWidth / ratio;
                const scaledHeight = canvasHeight / ratio;

                console.log('Scaling down canvas to: ' + parseInt(scaledWidth) + 'x' + parseInt(scaledHeight));

                canvas.setArtworkSize(scaledWidth, scaledHeight, false);
                canvas.setZoom(1 / ratio);
                canvas.fullVisibilityZoom = 1 / ratio;
            } else if (!minSizeAltered) {
                canvas.setZoom(1);
                canvas.setArtworkSize(canvasWidth, canvasHeight, false);
                canvas.fullVisibilityZoom = 1;
            }
            canvas.setGuidelinesMeasures(canvasWidth, canvasHeight);
        }

        function scaleCanvasElementsForPreview(
            canvas,
            elements,
            epsId = null,
            isChildTemplateProduct = false,
            fromImportLayout = false
        ) {
            canvas.discardActiveObject();

            const elementLeftSide = (el) => {
                const width = el.getWidth ? el.getWidth() : el.width;
                if (el.originX === 'center') {
                    return el.left - width / 2;
                }
                return el.left;
            };

            const elementRightSide = (el) => {
                const width = el.getWidth ? el.getWidth() : el.width;
                if (el.originX === 'center') {
                    return el.left + width / 2;
                }
                return el.left + width;
            };

            const elementTopSide = (el) => {
                const height = el.getHeight ? el.getHeight() : el.height;
                if (el.originY === 'center') {
                    return el.top - height / 2;
                }
                return el.top;
            };

            const elementBottomSide = (el) => {
                const height = el.getHeight ? el.getHeight() : el.height;
                if (el.originY === 'center') {
                    return el.top + height / 2;
                }
                return el.top + height;
            };

            // Calculate the group position from the group position in step 1
            const minX = elementLeftSide(_.min(elements, elementLeftSide));
            const maxX = elementRightSide(_.max(elements, elementRightSide));
            const minY = elementTopSide(_.min(elements, elementTopSide));
            const maxY = elementBottomSide(_.max(elements, elementBottomSide));

            let eps = ProductService.getEps(epsId);

            const step1GroupX = (maxX + minX) / 2;
            const step1GroupY = (maxY + minY) / 2;
            const canvasWidth = (canvas.getArtworkWidth() / canvas.getZoom()) * canvas.getEffectiveZoom();
            const canvasHeight = (canvas.getArtworkHeight() / canvas.getZoom()) * canvas.getEffectiveZoom();

            const groupX = fromImportLayout
                ? (step1GroupX * canvas.getArtworkWidth()) / (eps?.width || canvasWidth)
                : (step1GroupX * canvas.getArtworkWidth()) / eps?.width;
            const groupY = fromImportLayout
                ? (step1GroupY * canvas.getArtworkHeight()) / (eps?.height || canvasHeight)
                : (step1GroupY * canvas.getArtworkHeight()) / eps?.height;

            // Create a group with all the elements
            const group = new fabric.Group(elements, {
                originX: 'center',
                originY: 'center',
                left: groupX,
                top: groupY
            });

            // Calculate the scaling for the group so it fits in the canvas from this step
            const scale = fromImportLayout
                ? Math.min((eps?.width || canvasWidth) / group.width, (eps?.height || canvasHeight) / group.height)
                : Math.min(canvas.getArtworkWidth() / eps?.width, canvas.getArtworkHeight() / eps?.height);
            group.set('scaleX', scale);
            group.set('scaleY', scale);
            if (fromImportLayout) {
                group.set({
                    ...canvas.getCenter()
                });
            }
            group.setCoords();
            group.destroy();

            // Update each element width and height
            for (const canvasElement of elements) {
                //for some strange reason updateFromGroupScaling is overwriting the vector clipPath
                //so we'll store the original clipPath on a variable and apply it in case we are dealing with a vector
                const clipPath = canvasElement.clipPath;
                canvasElement.updateFromGroupScaling();

                switch (canvasElement.type) {
                    case 'bounded-text':
                        {
                            // Update font sizes for the new element size
                            const { min, max } = GeneralMathUtils.calculateFontSizesFromObject(canvasElement);
                            canvasElement.setMinSizePx(min);
                            canvasElement.setMaxSizePx(max);
                            canvasElement.setOutlineWidth(
                                GeneralMathUtils.calculateStrokeSizeFromObject(canvasElement)
                            );
                            if (canvasElement.allowEdit && canvasElement.originalInitCenterX != null) {
                                const values = GeneralMathUtils.calculateInitTextValuesFromObject(canvasElement);
                                canvasElement.setInitPositionAndSize(
                                    values.initCenterX,
                                    values.initCenterY,
                                    values.initFontSize
                                );
                            }
                        }
                        break;

                    case 'text-transform': {
                        const strokeWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                            canvasElement.width,
                            canvasElement.width,
                            canvasElement.getStrokeWidth()
                        );
                        canvasElement.set('strokeWidth', strokeWidth);
                        break;
                    }

                    case 'path-itext': {
                        const { min, max } = GeneralMathUtils.calculateFontSizesFromDimensions(
                            canvasElement.horizontalDiameter,
                            canvasElement.originalHorizontalDiameter,
                            canvasElement.originalMinSizePx,
                            canvasElement.originalMaxSizePx
                        );
                        canvasElement.set('minSizePx', min);
                        canvasElement.set('maxSizePx', max);
                        const fontSize = GeneralMathUtils.calculateCircularFontSizeFromDimensions(
                            canvasElement.horizontalDiameter,
                            canvasElement.originalHorizontalDiameter,
                            canvasElement.originalFontSize
                        );
                        canvasElement.set('fontSize', fontSize);
                        const strokeWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                            canvasElement.horizontalDiameter,
                            canvasElement.originalHorizontalDiameter,
                            canvasElement.getOutlineWidth()
                        );
                        canvasElement.set('strokeWidth', strokeWidth);
                        break;
                    }
                    case 'vector-placeholder': {
                        canvasElement.clipPath = clipPath;
                    }
                }
                if (isChildTemplateProduct && (canvasElement.isPrintArea || canvasElement.isProductGuidelines)) {
                    canvasElement.set({
                        ...canvas.getCenter()
                    });
                } else {
                    canvasElement.setCoords();
                }
            }
        }

        return {
            applyCanvasScaling: applyCanvasScaling,
            scaleCanvasElementsForPreview: scaleCanvasElementsForPreview
        };
    }
})();
